<template>
    <div class="see-more" :data-testid="`${props.dataTestId}-content`">
        <div
            ref="contentElement"
            class="see-more__content"
            :class="[collapsed ? 'see-more--collapsed' : 'see-more--expanded', props.htmlContent && collapsed ? 'see-more--html-content' : '']"
        >
            <slot />
        </div>
        <ClientOnly>
            <template v-if="isVisible">
                <VJoyLink v-if="!isLocked" class="see-more__button" :text="label" rel="nofollow" @click="handleClick" />
                <VJoyLink
                    v-else
                    class="see-more__button"
                    :text="locked?.label"
                    :href="lockedHref"
                    icon="lock"
                    rel="nofollow"
                    @click="handleClickOnLock"
                ></VJoyLink>
            </template>
            <template #fallback>
                <VJoyFormFieldSkeleton class="see-more__button" size="xxsmall" width="80px" />
            </template>
        </ClientOnly>
    </div>
</template>
<script setup lang="ts">
    import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useLocalePrefix, useRoute, useTranslation} from '#imports';
    import {VJoyFormFieldSkeleton, VJoyLink} from '@maltjoy/core-vue';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';

    export type SeeMoreProps = {
        maxLines?: number;
        dataTestId?: string;
        content?: string;
        htmlContent?: boolean;
        locked?:
            | {allowHref: false; label: string} // No href, event sent
            | {allowHref: true; label: string; componentName: string; targetId: string}; // default href
        defaultState?: 'collapsed' | 'expanded';
    };

    const props = withDefaults(defineProps<SeeMoreProps>(), {
        maxLines: 4,
        dataTestId: 'see-more-component',
        defaultState: 'collapsed',
        htmlContent: false,
    });

    const {t} = useTranslation();
    const {withLocalePrefix} = useLocalePrefix();
    const route = useRoute();
    const emits = defineEmits(['seeMoreClickedWhenLocked']);

    // Expand/Collapse
    const maxLinesCssVariable = computed(() => `${props.maxLines}`);
    const collapsed = ref<boolean>(props.defaultState === 'collapsed');
    const label = computed(() => (collapsed.value ? t('profile.section.see.more') : t('profile.section.see.less')));
    function handleClick(e: MouseEvent) {
        e.preventDefault();
        collapsed.value = !collapsed.value;
    }

    // Visibility
    const contentElement = ref<HTMLElement>();
    const isVisible = ref<boolean>(true);

    // JS-computed max-height for HTML content (display:block path).
    const htmlContentMaxHeight = ref<string>(`${props.maxLines * 1.6}em`);
    function updateHtmlContentMaxHeight() {
        if (!contentElement.value || !props.htmlContent || !collapsed.value) return;
        const lineHeight = parseFloat(window.getComputedStyle(contentElement.value).lineHeight);
        if (!isNaN(lineHeight) && lineHeight > 0) {
            htmlContentMaxHeight.value = `${Math.ceil(lineHeight * props.maxLines)}px`;
        }
    }

    async function refresh() {
        if (props.htmlContent && collapsed.value) {
            updateHtmlContentMaxHeight();
        }
        await nextTick();
        computeVisibility();
    }

    watch(() => props.content, refresh);
    watch(() => [props.maxLines, props.htmlContent], refresh);
    watch(collapsed, refresh);

    async function handleResize() {
        await refresh();
    }

    onMounted(async () => {
        await refresh();
        window.addEventListener('resize', handleResize);
    });
    onUnmounted(() => window.removeEventListener('resize', handleResize));

    function computeVisibility() {
        if (!contentElement.value || !collapsed.value) return;
        isVisible.value = contentElement.value.scrollHeight > contentElement.value.clientHeight;
    }

    // Lock
    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());
    const isLocked = computed(() => !!props.locked && sectionVisibility.value[SectionsVisibilityType.VIEW_MORE_LOCK]);
    const lockedHref = computed(() => {
        if (!props.locked?.allowHref) {
            return undefined;
        }
        const redirectParam = encodeURIComponent(`${route.path}#${props.locked.targetId}`);
        return withLocalePrefix(`/who-are-you?redirect=${redirectParam}&pageSource=profile_page&componentSource=${props.locked.componentName}`);
    });
    function handleClickOnLock() {
        emits('seeMoreClickedWhenLocked');
    }
</script>
<style scoped lang="scss">
    .see-more {
        &__button {
            margin-top: var(--joy-core-spacing-2);
            font-weight: normal;
        }

        &--collapsed {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: pre-wrap;

            // Plain text: use native line-clamping.
            &:not(.see-more--html-content) {
                display: -webkit-box;
                -webkit-line-clamp: v-bind(maxLinesCssVariable);
                -webkit-box-orient: vertical;
            }

            // HTML content: Safari ignores display:block when display:-webkit-box is also
            // declared on the same element (even at lower specificity), causing block-level
            // children to escape the overflow context and overlap sibling elements.
            // Keeping the two display modes in mutually-exclusive rules avoids the conflict.
            &.see-more--html-content {
                display: block;
                white-space: normal;
                max-height: v-bind(htmlContentMaxHeight);
            }
        }

        &--expanded {
            white-space: pre-wrap;
            max-height: none;
            overflow: visible;
        }
    }
</style>
